import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {Text, TextArea} from '@gravity-ui/uikit';
import {Pencil, TrashBin} from '@gravity-ui/icons';

import './RowCommentCell.scss';

const b = block('favorite-price-lists-row-comment');

export type RowCommentCellRow = {
    id: string;
    comment?: string;
};

export type RowCommentCellProps = {
    row: RowCommentCellRow;
    editingRowId: string | null;
    onStartEdit: (rowId: string) => void;
    onFinishEdit: (rowId: string) => void;
    onChangeComment: (rowId: string, value: string) => void;
    onClearComment: (rowId: string) => void;
};

export const RowCommentCell: React.FC<RowCommentCellProps> = ({
    row,
    editingRowId,
    onStartEdit,
    onFinishEdit,
    onChangeComment,
    onClearComment
}) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const wasEditingRef = useRef(false);
    const value = row.comment;
    const hasSaved = Boolean(value?.trim());
    const showEditor = !hasSaved || editingRowId === row.id;

    const commit = useCallback(() => {
        onFinishEdit(row.id);
    }, [onFinishEdit, row.id]);

    const resizeToContent = useCallback(() => {
        const el = textareaRef.current;
        if (!el) {
            return;
        }
        el.style.height = '0';
        el.style.height = `${el.scrollHeight}px`;
    }, []);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            textareaRef.current?.blur();
        }
    }, []);

    useLayoutEffect(() => {
        if (!showEditor) {
            wasEditingRef.current = false;
            return;
        }
        resizeToContent();
    }, [showEditor, value, resizeToContent]);

    useLayoutEffect(() => {
        const editing = editingRowId === row.id;
        if (!editing) {
            wasEditingRef.current = false;
            return;
        }
        if (wasEditingRef.current) {
            return;
        }
        wasEditingRef.current = true;
        const el = textareaRef.current;
        if (!el) {
            return;
        }
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
        resizeToContent();
    }, [editingRowId, row.id, resizeToContent]);

    return (
        <div className={b()}>
            {!showEditor ? (
                <div className={b('comment-view')}>
                    <Text variant="body-2" className={b('comment-body')}>
                        {value}
                    </Text>

                    <Pencil
                        onClick={() => onStartEdit(row.id)}
                        className={b('icon')}
                        role="button"
                        tabIndex={0}
                        aria-label="Редактировать комментарий"
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onStartEdit(row.id);
                            }
                        }}
                    />
                </div>
            ) : (
                <div className={b('comment-editor')}>
                    <TextArea
                        size="xl"
                        className={b('comment-textarea')}
                        placeholder="Ваш комментарий"
                        value={value}
                        minRows={1}
                        onFocus={() => onStartEdit(row.id)}
                        onUpdate={(v) => onChangeComment(row.id, v)}
                        onBlur={commit}
                        onKeyDown={handleKeyDown}
                        controlRef={textareaRef}
                        controlProps={{
                            style: {overflow: 'hidden', resize: 'none'}
                        }}
                    />
                    {value?.trim() ? (
                        <div
                            className={b('comment-trash-host')}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            <TrashBin
                                onClick={() => onClearComment(row.id)}
                                className={b('icon')}
                                role="button"
                                tabIndex={0}
                                aria-label="Удалить комментарий"
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onClearComment(row.id);
                                    }
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};
